import { Checkbox, InputAdornment } from '@material-ui/core';
import {
  AutocompleteClassKey,
  AutocompleteProps,
} from '@material-ui/lab/Autocomplete';
import clsx from 'clsx';
import { Avatar, Body, Chip, Icon } from 'copilot-design-system';
import { useFormikContext } from 'formik';
import React, { ComponentProps } from 'react';

import Autocomplete from 'src/components/Autocomplete';
import { channelMemberOptionTypes } from 'src/constants/multiSelectConsts';
import { ChevronDownIcon } from 'src/legacy/components/Icons';
import {
  BaseTextField,
  TextFieldSizeVariant,
} from 'src/legacy/components/TextField';
import { NonHoverBorder } from 'src/theme/colors';
import { getInitials } from 'src/utils/UserUtils';

import { GroupListboxComponent, RenderGroup } from './GroupListBox';
import { ListboxComponent } from './ListBox';

const icon = <div className="h-4 w-4 border border-solid rounded-sm" />;
const checkedIcon = (
  <div className="flex items-center justify-center h-4 w-4 bg-white rounded-sm">
    <Icon icon="Check" className="h-3 w-3 text-secondary" />
  </div>
);

// ComboBoxOption can be used for many types of multi-selects
// so it contains properties that other select can use
export type ComboBoxOption = {
  label: string;
  type: channelMemberOptionTypes | 'email';
  id: string;
  companyId?: string;
  getstreamId?: string;
  avatar?: string;
  fallbackColor?: string;
  companyName?: string;
  group?: string;
  deleteIconStyle?: React.CSSProperties;
  isPlaceholder?: boolean;
};

export interface ChannelMemberSelectComboBoxOption extends ComboBoxOption {
  type: channelMemberOptionTypes;
}

interface AdditionalComboBoxProps {
  openOnFocus?: boolean;
  inputProps?: InputProps;
}

interface InputProps {
  value?: string;
  autoFocus?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface ComboBoxOwnProps {
  id: string;
  label: string;
  placeholder?: string;
  values: any[] | any;
  additionalComboBoxProps?: AdditionalComboBoxProps;
  withAvatars?: boolean;
  withChips?: boolean;
  showPlaceholder?: boolean;
  hideSelectedItems?: boolean;
  error?: boolean;
  helperText?: any;
  disabled?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  textFieldVariant?: TextFieldSizeVariant;
  disabledIDs?: string[];
  autoCompleteClasses?: Partial<Record<AutocompleteClassKey, string>>;
  open?: boolean; // Controls the open state of auto-complete
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  popupIcon?: React.ReactNode;
  noOptionsText?: string | React.ReactNode;
  closeIcon?: React.ReactNode;
  blurOnSelect?: boolean;
  fieldName?: string;
}

export type ComboBoxProps<
  Option extends ComboBoxOption = ComboBoxOption,
  Multiple extends boolean = true,
> = ComboBoxOwnProps &
  Pick<
    AutocompleteProps<Option, Multiple, Multiple, undefined>,
    'onChange' | 'options' | 'multiple' | 'groupBy'
  > &
  Pick<
    Partial<AutocompleteProps<Option, Multiple, Multiple, undefined>>,
    'filterOptions' | 'getOptionLabel'
  >;

function getCustomListboxComponent<
  Option extends ComboBoxOption,
  Multiple extends boolean,
>({ groupBy }: Pick<ComboBoxProps<Option, Multiple>, 'groupBy'>) {
  return React.forwardRef(
    (
      data: React.HTMLAttributes<HTMLDivElement>,
      ref: React.Ref<HTMLDivElement>,
    ) =>
      groupBy ? (
        <GroupListboxComponent ref={ref} {...data} />
      ) : (
        <ListboxComponent ref={ref} {...data} />
      ),
  );
}

function ComboBox<Option extends ComboBoxOption, Multiple extends boolean>({
  id,
  label,
  placeholder,
  showPlaceholder = true,
  options,
  values,
  onChange,
  onBlur,
  additionalComboBoxProps,
  filterOptions,
  withAvatars = false,
  withChips = false,
  hideSelectedItems,
  multiple,
  error,
  helperText,
  disabled = false,
  textFieldVariant = 'skinny',
  disabledIDs = [],
  autoCompleteClasses = {},
  onKeyDown,
  open,
  popupIcon,
  groupBy,
  noOptionsText,
  closeIcon,
  blurOnSelect,
  fieldName,
}: ComboBoxProps<Option, Multiple>) {
  const { handleBlur } = useFormikContext();
  const getInputProps = (): InputProps => {
    if (additionalComboBoxProps) {
      if (additionalComboBoxProps.inputProps) {
        return additionalComboBoxProps.inputProps;
      }
    }
    return {};
  };

  const getOpenProps = () => (open !== undefined ? { open } : {});

  // openOnFocus indicates whether the dropdown list should open on focus or not
  const { openOnFocus = false } = additionalComboBoxProps || {};
  const getInputValueProp = () =>
    disabled && { inputValue: values ? values.label : '' };

  const selectedOptionsRenderer = (
    opts: ComboBoxOption[],
    getTagProps: any,
  ) => {
    return (
      <div className="flex flex-wrap gap-[5px] py-[5px] px-0">
        {opts.map((option: ComboBoxOption, index: number) => {
          const {
            onDelete: onClose,
            disabled: isChipDisabled,
            key,
          } = getTagProps({ index });
          return (
            <Chip
              label={option.label}
              variant="outlined"
              prefixIcon={
                withAvatars && (
                  <Avatar
                    variant={option.type === 'company' ? 'rounded' : 'circle'}
                    text={getInitials(option.label, {
                      twoLetters: option.type === 'client',
                    })}
                    src={option.avatar}
                    fallbackColor={option.fallbackColor ?? ''}
                    size="2xs"
                    alt={option.label}
                  />
                )
              }
              onClose={onClose}
              disabled={isChipDisabled}
              key={key}
            />
          );
        })}
      </div>
    );
  };

  const isOptionDisabled = (option: ComboBoxOption) => {
    return (
      option.type === 'title' ||
      option.type === 'no-option' ||
      disabledIDs.some((disabledID) => option.id.indexOf(disabledID) > -1)
    );
  };

  const optionItemRenderer = (
    option: ComboBoxOption,
    { selected }: { selected: ComponentProps<typeof Checkbox>['checked'] },
  ) => {
    const noOption = option.type === 'no-option';

    if (noOption) {
      return (
        // no-option is a disabled option, and thus gets 0.6 opacity
        // applied to it by MUI. We use a darker font to offset this.
        <Body className="text-black text-[12px] opacity-100">
          {`No matches found for "${option.label}"`}
        </Body>
      );
    }

    if (withChips) {
      return <Chip label={option.label} />;
    }

    return (
      <div className="flex items-center w-full">
        {withAvatars && !noOption ? (
          <div className="mr-2">
            <Avatar
              src={option.avatar ?? ''}
              fallbackColor={option.fallbackColor ?? ''}
              variant={option.type === 'company' ? 'rounded' : 'circle'}
              text={getInitials(option.label, {
                twoLetters: option.type === 'client',
              })}
              size="sm"
              style={{
                border: `1px solid ${NonHoverBorder}`,
                boxSizing: 'border-box',
              }}
              alt={option.label}
            />
          </div>
        ) : (
          <>
            {!noOption && (
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
            )}
          </>
        )}

        <div>
          <Body className="overflow-hidden overflow-ellipsis whitespace-nowrap max-w-[360px] text-black-heading text-[14px]">
            {option.label}
          </Body>
          {option.companyName && (
            <Body className="text-black-heading text-[14px]">
              {option.companyName || ''}
            </Body>
          )}
        </div>
      </div>
    );
  };

  const inputRenderer: ComponentProps<typeof Autocomplete>['renderInput'] = (
    params,
  ) => {
    if (values?.label) {
      params.InputProps.startAdornment = (
        <>
          <InputAdornment position="start" className="ml-[5px] mr-0">
            <Avatar
              src={values.avatar}
              fallbackColor={values.fallbackColor}
              variant={values.type === 'company' ? 'rounded' : 'circle'}
              text={getInitials(values.label, {
                twoLetters: values.type === 'client',
              })}
              size="sm"
              alt={values.label}
              style={{
                border: `1px solid ${NonHoverBorder}`,
                boxSizing: 'border-box',
              }}
            />
          </InputAdornment>

          {params.InputProps.startAdornment}
        </>
      );
    }

    return (
      <BaseTextField
        sizeVariant={textFieldVariant}
        {...params}
        label={label}
        name={fieldName}
        variant="outlined"
        placeholder={showPlaceholder ? placeholder || `Choose ${label}` : ''}
        fullWidth
        {...getInputProps()}
        error={error}
        helperText={helperText}
        disabled={disabled}
        inputProps={{
          ...params.inputProps,
          isautocomplete: 'true',
        }}
        InputLabelProps={{
          classes: {
            root: clsx({ '!text-non-hover-border': disabled }),
          },
        }}
        onBlur={handleBlur}
      />
    );
  };

  return (
    <Autocomplete<Option, Multiple, Multiple>
      blurOnSelect={blurOnSelect}
      limitTags={2}
      classes={{
        listbox: 'py-1.5 px-0 ',
        paper:
          'border border-solid border-non-hover-border shadow-dropdown-menu',
        groupLabel: 'text-gray-small font-medium',
        input: clsx(
          'placeholder:text-[#9B9FA3] [&&&&::placeholder]:!opacity-100',
          {
            'text-[14px]': true,
            'text-hover-border': disabled,
            'text-gray-400 text-[16px] sm:text-[13px]': false,
          },
        ),
        endAdornment: 'mr-0.5',

        ...autoCompleteClasses,
      }}
      multiple={multiple}
      id={id}
      options={options}
      value={values}
      {...getInputValueProp()}
      {...getOpenProps()}
      onBlur={onBlur}
      filterOptions={filterOptions}
      onChange={onChange}
      onKeyDown={onKeyDown}
      popupIcon={
        popupIcon !== undefined ? (
          popupIcon
        ) : (
          <ChevronDownIcon
            className={clsx('h-[15px] w-[15px]', {
              'text-non-hover-border': disabled,
              'text-gray-small': !disabled,
            })}
          />
        )
      }
      ListboxComponent={getCustomListboxComponent({ groupBy })}
      renderGroup={RenderGroup}
      groupBy={groupBy}
      // to shrink row size for color chips list or small avatars
      ListboxProps={{
        chipsized: String(withChips),
      }}
      renderTags={selectedOptionsRenderer}
      getOptionLabel={(option) => option.label}
      getOptionSelected={(option: ComboBoxOption) =>
        multiple
          ? values.find((item: any) => item.id === option.id)
          : values.id === option.id
      }
      renderOption={optionItemRenderer}
      renderInput={inputRenderer}
      disableClearable={multiple}
      openOnFocus={openOnFocus}
      closeIcon={closeIcon}
      filterSelectedOptions={hideSelectedItems}
      disabled={disabled}
      getOptionDisabled={(option) => isOptionDisabled(option)}
      noOptionsText={noOptionsText}
    />
  );
}

export default ComboBox;
